import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Table,
  Badge,
  Form,
} from 'react-bootstrap';
import { FaArrowLeft, FaFileExcel, FaSearch, FaExclamationTriangle } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import { reporteApi } from '../../services/api/reporteApi';
import { clienteApi } from '../../services/api/clienteApi';
import { downloadExcelReport } from '../../services/utils/downloadHelper';
import 'react-toastify/dist/ReactToastify.css';

const hoyIso = () => new Date().toISOString().split('T')[0];
const hace30DiasIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
};

const ReporteServiciosDuplicadosComensales = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filas, setFilas] = useState([]);
  const [totalCasos, setTotalCasos] = useState(0);
  const [clientes, setClientes] = useState([]);

  const [fechaDesde, setFechaDesde] = useState(hace30DiasIso());
  const [fechaHasta, setFechaHasta] = useState(hoyIso());
  const [idCliente, setIdCliente] = useState('');
  const [documento, setDocumento] = useState('');
  const [estado, setEstado] = useState('');

  useEffect(() => {
    if (!token || !selectedProject) navigate('/');
  }, [navigate, token, selectedProject]);

  useEffect(() => {
    if (!selectedProject?.id) return;
    clienteApi.getByProyectoRest(selectedProject.id).then((res) => {
      const lista = res.data?.data || res.data || [];
      setClientes(Array.isArray(lista) ? lista : []);
    }).catch(() => setClientes([]));
  }, [selectedProject?.id]);

  const buildParams = () => {
    const params = {};
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;
    if (idCliente) params.id_cliente = parseInt(idCliente, 10);
    if (documento.trim()) params.documento = documento.trim();
    if (estado) params.estado = estado;
    return params;
  };

  const buscar = useCallback(async () => {
    setLoading(true);
    setFilas([]);
    setTotalCasos(0);
    try {
      const res = await reporteApi.listarServiciosDuplicadosComensales(buildParams());
      if (res.data?.success) {
        setFilas(res.data.filas || []);
        setTotalCasos(res.data.total_casos || 0);
        if ((res.data.filas || []).length === 0) {
          toast.info('Sin registros en auditoría con esos filtros.');
        } else {
          toast.success(`${res.data.total_casos} caso(s), ${res.data.filas.length} fila(s).`);
        }
      } else {
        toast.error(res.data?.message || 'Error al cargar reporte.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error al consultar.');
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta, idCliente, documento, estado]);

  const exportarExcel = async () => {
    setExporting(true);
    try {
      await downloadExcelReport(
        reporteApi.descargarServiciosDuplicadosComensalesExcel,
        buildParams(),
        `servicios_duplicados_comensales_${hoyIso()}.xlsx`
      );
      toast.success('Excel descargado.');
    } catch (err) {
      toast.error(err.message || 'Error al exportar.');
    } finally {
      setExporting(false);
    }
  };

  const estilosDh = `
    .dh-reporte-bg { background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); min-height: 100vh; }
    .dh-main-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white; padding: 2rem 0; margin-bottom: 2rem;
      border-radius: 0 0 30px 30px; box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
    }
    .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
    .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
    .dh-card { border: none; border-radius: 20px; box-shadow: 0 15px 35px rgba(0,0,0,0.1); background: white; }
    .dh-card-header {
      border-radius: 20px 20px 0 0; padding: 1.25rem 1.75rem; border: none;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; font-weight: 700;
    }
    .btn-dh-primary {
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
    }
    .btn-dh-primary:hover { color: white; filter: brightness(1.06); }
    .btn-dh-secondary {
      background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
      border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
    }
    .dh-form-label { font-weight: 600; color: #374151; }
    .dh-form-control { border-radius: 10px; }
    .dh-alert-info { background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 12px; color: #3730a3; }
  `;

  return (
    <>
      <style>{estilosDh}</style>
      <div className="dh-reporte-bg">
        <ToastContainer position="top-right" />
        <div className="dh-main-header">
          <Container>
            <Row className="align-items-center">
              <Col>
                <h1 className="dh-main-title">
                  <FaExclamationTriangle className="me-2" />
                  Reporte servicios duplicados
                </h1>
                <p className="dh-main-subtitle">
                  Historial de comensales que consumieron el mismo servicio en varios comedores el
                  mismo día. Los casos se registran al buscar en Consultas → Servicios duplicados.
                </p>
              </Col>
              <Col xs="auto" className="d-flex gap-2">
                <Button className="btn-dh-primary" onClick={exportarExcel} disabled={exporting}>
                  {exporting ? <Spinner size="sm" /> : <FaFileExcel className="me-2" />}
                  Excel
                </Button>
                <Button className="btn-dh-secondary" onClick={() => navigate(-1)}>
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="pb-5">
          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header">Filtros</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={2}>
                  <Form.Label className="dh-form-label">Desde</Form.Label>
                  <Form.Control type="date" className="dh-form-control" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
                </Col>
                <Col md={2}>
                  <Form.Label className="dh-form-label">Hasta</Form.Label>
                  <Form.Control type="date" className="dh-form-control" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Cliente</Form.Label>
                  <Form.Select className="dh-form-control" value={idCliente} onChange={(e) => setIdCliente(e.target.value)}>
                    <option value="">Todos</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="dh-form-label">Documento</Form.Label>
                  <Form.Control className="dh-form-control" value={documento} onChange={(e) => setDocumento(e.target.value)} />
                </Col>
                <Col md={2}>
                  <Form.Label className="dh-form-label">Estado</Form.Label>
                  <Form.Select className="dh-form-control" value={estado} onChange={(e) => setEstado(e.target.value)}>
                    <option value="">Todos</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="corregido">Corregido</option>
                  </Form.Select>
                </Col>
              </Row>
              <div className="mt-3">
                <Button className="btn-dh-primary" onClick={buscar} disabled={loading}>
                  {loading ? <Spinner size="sm" className="me-2" /> : <FaSearch className="me-2" />}
                  Consultar
                </Button>
              </div>
              <Alert variant="info" className="dh-alert-info mt-3 mb-0 small">
                Proyecto: <strong>{selectedProject?.nombre}</strong>. Una fila por cada servicio
                duplicado registrado (detalle completo de comedores).
              </Alert>
            </Card.Body>
          </Card>

          <Card className="dh-card">
            <Card.Header className="dh-card-header d-flex justify-content-between">
              <span>Detalle</span>
              {totalCasos > 0 && <Badge bg="light" text="dark">{totalCasos} casos · {filas.length} filas</Badge>}
            </Card.Header>
            <Card.Body className="p-0">
              {filas.length === 0 ? (
                <p className="p-4 mb-0 text-muted">Sin datos. Ajuste filtros y pulse Consultar.</p>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0 small align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Estado</th>
                        <th>Cliente</th>
                        <th>Documento</th>
                        <th>Comensal</th>
                        <th>Fecha</th>
                        <th>Servicio</th>
                        <th>Comedor</th>
                        <th>Hora</th>
                        <th className="text-end">Precio</th>
                        <th>Eliminado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.map((f, i) => (
                        <tr key={`${f.id_auditoria}-${f.id_turno_detalle}-${i}`}>
                          <td>
                            <Badge bg={f.estado === 'corregido' ? 'success' : 'warning'} text="dark">
                              {f.estado}
                            </Badge>
                          </td>
                          <td>{f.nombre_cliente}</td>
                          <td>{f.numero_documento_identidad}</td>
                          <td>{f.nombre_comensal}</td>
                          <td>{f.fecha_servicio}</td>
                          <td>{f.tipo_servicio}</td>
                          <td>{f.comedor_nombre}</td>
                          <td>{f.fecha_registro_atencion}</td>
                          <td className="text-end">{Number(f.precio_venta || 0).toFixed(2)}</td>
                          <td className="text-center">{f.registro_eliminado ? 'Sí' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default ReporteServiciosDuplicadosComensales;
